// The dojo's view (gates B1, B2; phase B joined): the training dummies as figures on the bodies module
// (src/bodies/figures.ts: their motion, the action poses and hit reactions from the combat events, ragdolls on a
// defeat), the hero's weapon on its hand socket following the combat state (the hero's poses go on in the valley's
// look, src/valley/look.ts), and the impact director (src/feedback/: hit-stop by the hit's kind, flashes, shake and
// kick, particles, telegraphs draped on the floor, weapon trails). A view: it reads the sim and never writes it.
import * as THREE from "three";
import type { Figure } from "@rpg/figure";
import type { LowPolyLook } from "@rpg/look";
import type { CameraRig } from "@rpg/camera";
import type { EventBus } from "@rpg/kernel";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { GameEvents } from "../core";
import { mountFeedback, reduceMotionOf, type Feedback } from "../feedback";
import { mountFigureSet, sampleWeapon, type FigureSet } from "../bodies/figures";
import type { CombatContent } from "./content";
import type { Dojo } from "./dojo";

/** The view's handle keys. */
export interface CombatView {
  feedback: Feedback;
  figures: FigureSet;
  /** The hero's held attachment id. */
  held(): string | null;
  /** Per-frame view numbers since the last reset (the hit-stop spec reads them): steps taken and view dt. */
  frames(): { steps: number; dt: number; realDt: number; frozen: boolean; mode: "play" | "advance" }[];
  resetFrames(): void;
}

/** Mount the dojo's view; `warm` (the impact director's programs) runs inside the valley look's warm-up. */
export function mountCombatView(o: {
  runtime: Runtime;
  scene: THREE.Scene;
  look: LowPolyLook;
  content: SpikeContent;
  combat: CombatContent;
  dojo: Dojo;
  hero: { figure: Figure };
  events: EventBus<GameEvents>;
  rig: CameraRig | null;
  setting?: (id: string) => unknown;
}): CombatView {
  const reg = o.content.registry;
  const d = o.dojo;
  const attachments = new Map(reg.rows("attachments").map((a) => [a.id, a] as const));
  const heroEntity = d.state().hero;
  const probe = (x: number, z: number, fromY: number): number | null => d.rig.collision.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["terrain"] })?.point.y ?? null;

  // the hero's weapon on its socket, following the combat state's weapon
  let held: string | null = null;
  const hold = (weapon: string | null): void => {
    const id = weapon ? (o.combat.attachmentOf.get(weapon) ?? null) : null;
    if (id === held) return;
    held = id;
    o.hero.figure.attach(id ? ((attachments.get(id) as never) ?? null) : null);
  };
  hold(d.fighter(heroEntity)?.weapon ?? null);

  let figures: FigureSet | null = null;
  const feedback = mountFeedback({
    runtime: o.runtime,
    events: o.events,
    scene: o.scene,
    content: o.content,
    rig: o.rig,
    weaponOf: (owner) => (owner === heroEntity ? heroWeapon() : (figures?.weapon(owner) ?? null)),
    heightAt: (x, z) => probe(x, z, 40) ?? 0,
    motion: () => ({ shake: Number(o.setting?.("camera.shake") ?? 1), reduceMotion: reduceMotionOf(o.setting?.("accessibility.reduceMotion")) }),
  });
  feedback.track(heroEntity, o.hero.figure.root);

  // the dummies, from their fighters' cards, on the bodies module
  figures = mountFigureSet({
    runtime: o.runtime,
    scene: o.scene,
    look: o.look,
    content: o.content,
    bodies: () => d.bodies,
    collision: () => d.rig.collision,
    entries: () => {
      const at = d.pose(heroEntity).feet;
      return d.state().dummies.map((m) => ({
        id: m.entity,
        card: o.combat.fighters.get(m.fighter)!.card.id,
        pos: m.feet,
        yaw: m.yaw,
        vel: [0, 0, 0] as const,
        lookAt: { x: at[0], y: at[1] + 1.4, z: at[2] },
      }));
    },
    probe,
    onAdd: (id, root) => feedback.track(id, root),
    onRemove: (id) => feedback.untrack(id),
    order: -14,
    name: "dojo view",
  });

  // the hero's weapon as the trails sample it: the socket's base and the attachment's far end
  const tips = new Map<string, THREE.Vector3>();
  const heroWeapon = () => (held ? sampleWeapon(o.hero.figure, held, tips) : null);

  const frames: { steps: number; dt: number; realDt: number; frozen: boolean; mode: "play" | "advance" }[] = [];
  o.runtime.on(
    "late",
    (c) => {
      frames.push({ steps: c.steps, dt: c.dt, realDt: c.realDt, frozen: o.runtime.clock.frozen, mode: c.mode });
      if (frames.length > 2000) frames.shift();
      if (c.mode === "play" && o.runtime.paused) return;
      hold(d.fighter(heroEntity)?.weapon ?? null);
    },
    { order: -14, name: "combat view: the hero's weapon" },
  );

  return {
    feedback,
    figures,
    held: () => held,
    frames: () => [...frames],
    resetFrames: () => void (frames.length = 0),
  };
}
